import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Order } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { localDb, formatCurrency } from '../lib/db'
import './Orders.css'

export default function Orders() {
    const { user } = useAuth()
    const [orders, setOrders] = useState<Order[]>([])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (user) {
            const userOrders = localDb.orders.getByUserId(user.id)
            setOrders(userOrders.sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            ))
        }
        setIsLoading(false)
    }, [user])

    const getStatusBadge = (status: Order['status']) => {
        const statusConfig = {
            pending: { label: 'Pending', class: 'badge-warning' },
            paid: { label: 'Paid', class: 'badge-primary' },
            ready: { label: 'Ready for Pickup', class: 'badge-success' },
            completed: { label: 'Completed', class: 'badge-success' },
            cancelled: { label: 'Cancelled', class: 'badge-error' }
        }
        const config = statusConfig[status] || statusConfig.pending
        return <span className={`badge ${config.class}`}>{config.label}</span>
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    if (isLoading) {
        return (
            <div className="container">
                <div className="orders-loading">
                    <div className="loader"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            <div className="orders-page">
                <h1 className="page-title">My Orders</h1>

                {orders.length === 0 ? (
                    <div className="empty-orders">
                        <span className="empty-icon">📦</span>
                        <h2>No orders yet</h2>
                        <p>Start shopping to see your orders here</p>
                        <Link to="/" className="btn btn-primary btn-lg">
                            Start Shopping
                        </Link>
                    </div>
                ) : (
                    <div className="orders-list">
                        {orders.map(order => (
                            <div key={order.id} className="order-card">
                                <div className="order-header">
                                    <div className="order-info">
                                        <span className="order-id">Order #{order.id.slice(0, 8).toUpperCase()}</span>
                                        <span className="order-date">{formatDate(order.created_at)}</span>
                                    </div>
                                    {getStatusBadge(order.status)}
                                </div>

                                <div className="order-items">
                                    {order.items?.map(item => (
                                        <div key={item.id} className="order-item">
                                            <span className="item-name">{item.product_name}</span>
                                            <span className="item-qty">x{item.quantity}</span>
                                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="order-footer">
                                    <div className="order-total">
                                        <span>Total:</span>
                                        <span className="total-amount">{formatCurrency(order.total)}</span>
                                    </div>

                                    {order.s_coins_used > 0 && (
                                        <div className="scoins-used">
                                            🪙 {order.s_coins_used} S-Coins used
                                        </div>
                                    )}

                                    {(order.status === 'paid' || order.status === 'ready') && (
                                        <div className="pickup-code-display">
                                            <span className="pickup-label">Pickup Code:</span>
                                            <span className="pickup-code">{order.pickup_code}</span>
                                        </div>
                                    )}
                                </div>

                                {order.status === 'completed' && order.s_coins_earned > 0 && (
                                    <div className="scoins-earned">
                                        <span>🪙 You earned {order.s_coins_earned} S-Coins!</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
